import React, { memo } from 'react'
import { Button } from '@sainsburys-tech/bolt'
import ImageCtaCard from '../../../helpers/ImageCtaCard/ImageCtaCard'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolContentDataAttributes } from '../../../helpers/tracking'
import {
  StyledWrapperMain,
  StyledWrapperWithLogo,
  StyledHeadingWrapper,
  StyledHeadingCtaWrapper,
  StyledContainer
} from './C004.styles'
import { C004Props } from './types'

/**
  C004 is a promotional component which can be used in various different ways:

  - Text only,
  - Main image with Heading and optional sub-heading,
  - Main image with logo
* */

const C004 = ({
  image = {
    url: '',
    altText: ''
  },
  link = {
    url: '',
    title: '',
    target: '',
    copy: ''
  },
  logo = {
    url: '',
    altText: ''
  },
  backgroundColor = 'white',
  borderColor,
  consumingGridType,
  dropShadow = false,
  imageStyle = 'short',
  heading,
  subheading,
  textColor = 'black',
  textAlignment = 'center',
  onLinkClick,
  positionInGrid,
  isOnlyComponentInGrid,
  roundel,
  citrus,
  legalCopy,
  placement
}: C004Props): React.ReactElement => {
  const ImageWrapper = logo?.url ? StyledWrapperWithLogo : StyledContainer
  const Wrapper = image?.url ? ImageWrapper : StyledWrapperMain
  const HeadingContainer = subheading ? StyledHeadingWrapper : StyledHeadingCtaWrapper

  const getGOLDataAttributes = (action?: string) =>
    createGolContentDataAttributes(
      consumingGridType,
      `C004 - ${positionInGrid !== undefined ? `Position${positionInGrid}` : 'NA'}`,
      link?.title,
      link?.url,
      heading,
      action
    )

  const addGolTracking = (event: React.MouseEvent) => {
    const dataAttributes = getGOLDataAttributes()
    Object.keys(dataAttributes).forEach((attribute) =>
      event.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event) => {
    if (onLinkClick) {
      onLinkClick(event, agnostifyUrl(link?.url))
    }
    addGolTracking(event)
  }

  return (
    <Wrapper
      {...getGOLDataAttributes('Load')}
      data-placement={placement || undefined}
      linkUrl={!!link?.url}
      dropShadow={dropShadow}
      textAlignment={textAlignment}
      borderColor={borderColor}
      textColor={textColor}
      imageStyle={imageStyle}
      data-test='c004-wrapper'
      stack='xs'
      gutterValue={0}
      href={link?.url ? agnostifyUrl(link?.url) : undefined}
      target={link?.url ? link?.target : undefined}
      onClick={(e) => (link?.url || onLinkClick) && ctaClick(e)}
      tabIndex={link?.url && 0}
      forwardedAs={link?.url ? 'a' : undefined}
      as={!link?.url && !!image?.url ? 'div' : undefined}
      hasOnLinkClick={!!onLinkClick}
    >
      {image?.url ? (
        <ImageCtaCard
          altText={image?.altText}
          logoAltText={logo?.altText}
          heading={heading}
          bodyCopy={subheading || undefined}
          imageStyle={imageStyle}
          mainImage={image?.url}
          logoImage={logo?.url || undefined}
          backgroundColor={backgroundColor || undefined}
          textAlignment={textAlignment}
          textColor={textColor}
          dataTest='c004-image-logo'
          roundel={roundel}
          isOnlyComponentInGrid={isOnlyComponentInGrid}
          consumingGridType={consumingGridType}
          citrus={citrus}
          legalCopy={legalCopy}
        />
      ) : (
        heading && <HeadingContainer data-test='c004-heading'>{heading}</HeadingContainer>
      )}
      {subheading && !image?.url && <p data-test='c004-subheading'>{subheading}</p>}

      {link?.copy && !citrus && (
        <Button
          as='span'
          kind='link'
          data-test='c004-cta'
          title={link?.title}
          href={link?.url ? agnostifyUrl(link?.url) : undefined}
          target={link?.url ? link?.target : undefined}
          style={{ alignSelf: 'center' }}
        >
          {link?.copy}
        </Button>
      )}
    </Wrapper>
  )
}

export default memo(C004)
